import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/chart";
import type { JiraMetricsTicket } from "@/http/api-types";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { formatTimelineValue, secondsToHours } from "./formatters";

export function TicketGanttChart({ ticket }: { ticket: JiraMetricsTicket }) {
  const inProgressHours = secondsToHours(ticket.total_time_in_progress_seconds);
  const inReviewHours = secondsToHours(ticket.total_time_in_review_seconds);
  const approvedToDoneHours =
    ticket.moved_to_changes_approved && ticket.moved_to_done
      ? Math.max(
          0,
          (new Date(ticket.moved_to_done).getTime() - new Date(ticket.moved_to_changes_approved).getTime()) / 3600000,
        )
      : 0;
  const hasApprovedToDone = approvedToDoneHours > 0;
  const xMax = Math.max(inProgressHours + inReviewHours, approvedToDoneHours, 1);

  const data = [
    {
      ticket_key: ticket.ticket_key,
      in_progress_hours: inProgressHours,
      in_review_hours: inReviewHours,
      approved_to_done_hours: approvedToDoneHours,
    },
  ];

  return (
    <ChartContainer
      className="h-28 w-full"
      config={{
        in_progress_hours: { label: "In Progress", color: "#22c55e" },
        in_review_hours: { label: "In Review", color: "#facc15" },
        approved_to_done_hours: { label: "Approved -> Done", color: "#38bdf8" },
      }}
    >
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        <CartesianGrid horizontal={false} />
        <XAxis type="number" domain={[0, xMax]} tickFormatter={value => formatTimelineValue(Number(value))} />
        <YAxis type="category" dataKey="ticket_key" width={90} />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(value, name) => (
                <div className="flex w-full items-center justify-between gap-3">
                  <span className="text-muted-foreground">
                    {name === "in_progress_hours"
                      ? "In Progress"
                      : name === "in_review_hours"
                        ? "In Review"
                        : "Approved -> Done"}
                  </span>
                  <span className="font-mono font-medium text-foreground">{formatTimelineValue(Number(value))}</span>
                </div>
              )}
            />
          }
        />
        <Bar dataKey="in_progress_hours" stackId="timeline" fill="var(--color-in_progress_hours)" />
        <Bar dataKey="in_review_hours" stackId="timeline" fill="var(--color-in_review_hours)" />
        {hasApprovedToDone && (
          <Bar dataKey="approved_to_done_hours" stackId="timeline" fill="var(--color-approved_to_done_hours)" />
        )}
      </BarChart>
    </ChartContainer>
  );
}
